import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Download,
  X,
  Search,
  Filter,
  ChevronUp,
  ChevronDown,
  FileText,
  RefreshCw,
} from 'lucide-react';

const LOG_LEVELS = ['ERROR', 'WARN', 'INFO', 'DEBUG', 'VERBOSE'];

function detectLogLevel(line) {
  // Match Android logcat format: "PID  TID E Tag:" where the level letter sits between TID and tag
  // e.g. "2026-04-14 10:21:26.969  1344  1344 D RemoteManagementController:"
  const logcatMatch = line.match(/\d+\s+\d+\s+([EWIDV])\s/);
  if (logcatMatch) {
    const letter = logcatMatch[1];
    if (letter === 'E') return 'ERROR';
    if (letter === 'W') return 'WARN';
    if (letter === 'I') return 'INFO';
    if (letter === 'D') return 'DEBUG';
    if (letter === 'V') return 'VERBOSE';
  }
  // Fallback: match "E/Tag" logcat format or full words
  if (/\bE\/|\bERROR\b/i.test(line)) return 'ERROR';
  if (/\bW\/|\bWARN(ING)?\b/i.test(line)) return 'WARN';
  if (/\bI\/|\bINFO\b/i.test(line)) return 'INFO';
  if (/\bD\/|\bDEBUG\b/i.test(line)) return 'DEBUG';
  if (/\bV\/|\bVERBOSE\b/i.test(line)) return 'VERBOSE';
  return null;
}

const LEVEL_COLORS = {
  ERROR: 'text-red-400',
  WARN: 'text-yellow-400',
  INFO: 'text-blue-400',
  DEBUG: 'text-green-400',
  VERBOSE: 'text-gray-400',
};

export default function LogViewer({ logContent, logName, loading, reloading, onClose, onReload }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState('search'); // 'search' or 'filter'
  const [activeFilters, setActiveFilters] = useState(new Set(LOG_LEVELS));
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const scrollContainerRef = useRef(null);
  const matchRefs = useRef([]);
  const hasScrolledToBottom = useRef(false);

  // Parse lines with their original indices
  const allLines = useMemo(() => {
    if (!logContent) return [];
    return logContent.split('\n').map((text, i) => ({
      text,
      originalIndex: i,
      level: detectLogLevel(text),
    }));
  }, [logContent]);

  // Filter lines by active log levels and text filter
  const filteredLines = useMemo(() => {
    const textFilter = searchMode === 'filter' && searchQuery.trim()
      ? searchQuery.toLowerCase()
      : null;
    return allLines.filter(line => {
      // Log level filter
      if (line.level && !activeFilters.has(line.level)) return false;
      // Text filter (only in filter mode)
      if (textFilter && !line.text.toLowerCase().includes(textFilter)) return false;
      return true;
    });
  }, [allLines, activeFilters, searchMode, searchQuery]);

  // Find search matches within filtered lines (only in search mode)
  const matchingLineIndices = useMemo(() => {
    if (searchMode !== 'search' || !searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return filteredLines
      .map((line, idx) => (line.text.toLowerCase().includes(query) ? idx : -1))
      .filter(idx => idx !== -1);
  }, [filteredLines, searchQuery, searchMode]);

  // Reset match index when search changes
  useEffect(() => {
    setCurrentMatchIndex(0);
  }, [searchQuery]);

  // Auto-scroll to bottom when content loads
  useEffect(() => {
    if (!loading && logContent && scrollContainerRef.current && !hasScrolledToBottom.current) {
      hasScrolledToBottom.current = true;
      // Small delay to ensure DOM has rendered all rows
      requestAnimationFrame(() => {
        const container = scrollContainerRef.current;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    }
  }, [loading, logContent]);

  // Reset scroll flag when new content is loading
  useEffect(() => {
    if (loading) {
      hasScrolledToBottom.current = false;
    }
  }, [loading]);

  // Navigate to current match
  useEffect(() => {
    if (matchingLineIndices.length > 0 && matchRefs.current[matchingLineIndices[currentMatchIndex]]) {
      matchRefs.current[matchingLineIndices[currentMatchIndex]]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [currentMatchIndex, matchingLineIndices]);

  const handleNextMatch = useCallback(() => {
    if (matchingLineIndices.length === 0) return;
    setCurrentMatchIndex(prev => (prev + 1) % matchingLineIndices.length);
  }, [matchingLineIndices]);

  const handlePrevMatch = useCallback(() => {
    if (matchingLineIndices.length === 0) return;
    setCurrentMatchIndex(prev => (prev - 1 + matchingLineIndices.length) % matchingLineIndices.length);
  }, [matchingLineIndices]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'f' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        document.getElementById('log-search-input')?.focus();
      } else if (e.key === 'Enter' && searchQuery) {
        if (e.shiftKey) handlePrevMatch();
        else handleNextMatch();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, searchQuery, handleNextMatch, handlePrevMatch]);

  const toggleFilter = (level) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(level)) next.delete(level);
      else next.add(level);
      return next;
    });
  };

  const handleDownload = () => {
    if (!logContent) return;
    const blob = new Blob([logContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${logName || 'device'}.log`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Highlight search/filter matches in a line
  const highlightLine = (text, lineIdx) => {
    if (!searchQuery.trim()) return text;
    const query = searchQuery.toLowerCase();
    const lowerText = text.toLowerCase();
    const idx = lowerText.indexOf(query);
    if (idx === -1) return text;

    const isCurrentMatch = searchMode === 'search' && matchingLineIndices[currentMatchIndex] === lineIdx;
    const highlightClass = searchMode === 'filter'
      ? 'bg-blue-400/30 rounded px-0.5'
      : isCurrentMatch ? 'bg-yellow-400 text-black rounded px-0.5' : 'bg-yellow-400/30 rounded px-0.5';
    return (
      <>
        {text.slice(0, idx)}
        <span className={highlightClass}>
          {text.slice(idx, idx + searchQuery.length)}
        </span>
        {text.slice(idx + searchQuery.length)}
      </>
    );
  };

  const lineCount = filteredLines.length;
  const totalLines = allLines.length;

  return (
    <div className="fixed inset-0 bg-black/70 flex flex-col z-50">
      {/* Header bar */}
      <div className="flex items-center gap-3 px-4 py-2 bg-background border-b flex-shrink-0">
        <FileText className="w-4 h-4 text-muted-foreground" />
        <span className="font-semibold text-sm truncate">{logName}</span>

        {!loading && (
          <span className="text-xs text-muted-foreground">
            {lineCount === totalLines ? `${totalLines} lines` : `${lineCount} / ${totalLines} lines`}
          </span>
        )}

        <div className="flex-1" />

        {onReload && (
          <Button variant="outline" size="sm" onClick={onReload} disabled={loading || reloading} className="gap-1.5 h-7 text-xs" title="Request latest log from device">
            <RefreshCw className={`w-3.5 h-3.5 ${reloading ? 'animate-spin' : ''}`} />
            {reloading ? 'Waiting for device...' : 'Reload'}
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={handleDownload} disabled={loading || !logContent} className="gap-1.5 h-7 text-xs">
          <Download className="w-3.5 h-3.5" />
          Download
        </Button>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0">
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Toolbar: search/filter + log levels */}
      {!loading && (
        <div className="flex items-center gap-2 px-4 py-1.5 bg-background/95 border-b flex-shrink-0 flex-wrap">
          {/* Search/Filter mode toggle */}
          <div className="flex items-center rounded-md border overflow-hidden h-7">
            <button
              onClick={() => setSearchMode('search')}
              className={`flex items-center gap-1 px-2 h-full text-xs transition-colors ${
                searchMode === 'search' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'
              }`}
              title="Search: highlight matching lines"
            >
              <Search className="w-3 h-3" />
              Search
            </button>
            <button
              onClick={() => setSearchMode('filter')}
              className={`flex items-center gap-1 px-2 h-full text-xs transition-colors ${
                searchMode === 'filter' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'
              }`}
              title="Filter: show only matching lines"
            >
              <Filter className="w-3 h-3" />
              Filter
            </button>
          </div>

          {/* Text input with clear button */}
          <div className="relative flex items-center">
            <Input
              id="log-search-input"
              placeholder={searchMode === 'search' ? 'Search logs... (Cmd+F)' : 'Filter logs...'}
              className="h-7 text-xs w-64 pr-7"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-1.5 text-muted-foreground hover:text-foreground"
                title="Clear"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Search navigation (only in search mode) */}
          {searchMode === 'search' && searchQuery && (
            <div className="flex items-center gap-0.5">
              <span className="text-xs text-muted-foreground whitespace-nowrap px-1">
                {matchingLineIndices.length > 0
                  ? `${currentMatchIndex + 1}/${matchingLineIndices.length}`
                  : 'No matches'}
              </span>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={handlePrevMatch} disabled={matchingLineIndices.length === 0}>
                <ChevronUp className="w-3.5 h-3.5" />
              </Button>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={handleNextMatch} disabled={matchingLineIndices.length === 0}>
                <ChevronDown className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}

          <div className="w-px h-5 bg-border mx-1" />

          {/* Log level filters */}
          <span className="text-xs text-muted-foreground">Level:</span>
          {LOG_LEVELS.map(level => (
            <button
              key={level}
              onClick={() => toggleFilter(level)}
              className={`px-2 py-0.5 rounded text-xs font-mono transition-opacity ${
                activeFilters.has(level) ? 'opacity-100' : 'opacity-30'
              } ${LEVEL_COLORS[level]} bg-muted hover:bg-muted/80`}
            >
              {level}
            </button>
          ))}
        </div>
      )}

      {/* Log content */}
      <div ref={scrollContainerRef} className="flex-1 overflow-auto bg-[#1a1a2e]">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-2 text-muted-foreground">Loading log...</span>
          </div>
        ) : (
          <table className="w-full font-mono text-xs leading-relaxed">
            <tbody>
              {filteredLines.map((line, idx) => {
                const isMatch = searchQuery && matchingLineIndices.includes(idx);
                const isCurrentMatch = matchingLineIndices[currentMatchIndex] === idx;
                return (
                  <tr
                    key={line.originalIndex}
                    ref={el => { matchRefs.current[idx] = el; }}
                    className={`
                      hover:bg-white/5
                      ${isCurrentMatch ? 'bg-yellow-400/15' : isMatch ? 'bg-yellow-400/5' : ''}
                      ${line.level === 'ERROR' ? 'bg-red-900/10' : ''}
                    `}
                  >
                    <td className="w-14 text-right text-gray-600 pr-3 py-px border-r border-gray-800 select-none sticky left-0 bg-[#1a1a2e] tabular-nums">
                      {line.originalIndex + 1}
                    </td>
                    <td className={`pl-3 py-px whitespace-pre break-all ${line.level ? LEVEL_COLORS[line.level] : 'text-gray-300'}`}>
                      {highlightLine(line.text, idx)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
